'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { v4 as uuidv4 } from 'uuid';
import { useCart } from '@weareconceptstudio/cart';
import { useAccountContext, useAddressContext, AddressForm } from '@weareconceptstudio/account';
import { useFormInstance } from '@weareconceptstudio/form';
import { api, useUi, useTranslation } from '@weareconceptstudio/core';
import { config } from '@/helpers';
import { closeBottomPopups } from '@/helpers/bottomPopups';
import { DEFAULT_ARMENIA_BBOX, fetchAddressSuggestions, formatAddressLabel, normalizeBranchText, resolveGeocodePrefill, resolveSuggestionPrefill } from './utils';

export const DELIVERY_SEARCH_FIELD = 'deliverySearch';

export const useDeliveryBar = (globalData) => {
	const router = useRouter();
	const { checkoutData, fillCheckoutData, getCart } = useCart();
	const { fulfillment, isFulfillmentEnabled, delivery, useUser } = useAccountContext();
	const { isLoggedIn } = useUser();
	const { addresses, selectedAddresses, selectAddress, hasAddressType, addressLoading } = useAddressContext();
	const { openPopup } = useUi();
	const { selectedLang, translate } = useTranslation();
	const { watch, setValue } = useFormInstance() || {};

	const isDeliveryEnabled = isFulfillmentEnabled || delivery?.enabled !== false;
	const branches = fulfillment?.branches || [];

	const [activeTab, setActiveTab] = useState(() => (checkoutData?.fulfillmentType === 'takeaway' ? 'takeaway' : 'delivery'));
	const [showDropdown, setShowDropdown] = useState(false);
	const [isLocating, setIsLocating] = useState(false);
	const [addressSuggestions, setAddressSuggestions] = useState([]);
	const [isSuggestLoading, setIsSuggestLoading] = useState(false);
	const [pendingAddress, setPendingAddress] = useState(() => {
		if (typeof window === 'undefined') return null;
		try {
			const raw = sessionStorage.getItem('pending_address');
			return raw ? JSON.parse(raw) : null;
		} catch {
			return null;
		}
	});

	const dropdownRef = useRef(null);
	const sessionTokenRef = useRef(null);
	const debounceTimeoutRef = useRef(null);
	const suggestAbortRef = useRef(null);
	const selectedLangRef = useRef(selectedLang);
	selectedLangRef.current = selectedLang;

	const searchQuery = watch?.(DELIVERY_SEARCH_FIELD) || '';
	const isTakeaway = activeTab === 'takeaway';
	const isSearching = searchQuery.trim().length > 0;

	const addressList = useMemo(() => {
		if (hasAddressType) return addresses?.shipping || [];
		return Array.isArray(addresses) ? addresses : [];
	}, [hasAddressType, addresses]);

	const selectedAddress = useMemo(() => {
		if (hasAddressType) return selectedAddresses?.shipping || null;
		return selectedAddresses || null;
	}, [hasAddressType, selectedAddresses]);

	const selectedBranch = useMemo(() => branches.find((b) => Number(b.id) === Number(checkoutData?.fulfillmentBranchId)), [branches, checkoutData?.fulfillmentBranchId]);

	const syncSearchFieldValue = useCallback(
		(value = '') => {
			setValue?.(DELIVERY_SEARCH_FIELD, value);
		},
		[setValue]
	);

	const getSessionToken = useCallback(() => {
		if (!sessionTokenRef.current) sessionTokenRef.current = uuidv4();
		return sessionTokenRef.current;
	}, []);

	const resetSessionToken = useCallback(() => {
		sessionTokenRef.current = null;
	}, []);

	const clearSuggestions = useCallback(() => {
		if (suggestAbortRef.current) suggestAbortRef.current.abort();
		if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
		setAddressSuggestions([]);
		setIsSuggestLoading(false);
	}, []);

	const closeDropdown = useCallback(() => {
		setShowDropdown(false);
		clearSuggestions();
		syncSearchFieldValue(selectedAddress ? formatAddressLabel(selectedAddress) : '');
	}, [clearSuggestions, selectedAddress, syncSearchFieldValue]);

	const focusSearchInput = useCallback(() => {
		dropdownRef.current?.querySelector('.selector-field input')?.focus();
	}, []);

	const openAddressFormPopup = useCallback(
		(prefill = {}) => {
			closeDropdown();
			syncSearchFieldValue('');

			openPopup(
				<AddressForm
					title='account.address_management.addNewAddress'
					data={prefill}
					type={hasAddressType ? 'shipping' : undefined}
				/>,
				{ className: 'popup-address-form-block' }
			);
		},
		[closeDropdown, hasAddressType, isLoggedIn, openPopup, syncSearchFieldValue]
	);

	const fetchSuggestions = useCallback(
		async (text) => {
			if (!text.trim()) {
				clearSuggestions();
				return;
			}

			if (suggestAbortRef.current) suggestAbortRef.current.abort();
			const controller = new AbortController();
			suggestAbortRef.current = controller;
			setIsSuggestLoading(true);

			try {
				const results = await fetchAddressSuggestions({
					text,
					lang: selectedLangRef.current,
					bbox: DEFAULT_ARMENIA_BBOX,
					sessionToken: getSessionToken(),
					signal: controller.signal,
				});
				setAddressSuggestions(results);
			} catch (err) {
				if (err.name !== 'AbortError') setAddressSuggestions([]);
			} finally {
				setIsSuggestLoading(false);
			}
		},
		[clearSuggestions, getSessionToken]
	);

	const handleTabChange = useCallback(
		(tab) => {
			setActiveTab(tab);
			syncSearchFieldValue('');
			closeDropdown();
		},
		[closeDropdown, syncSearchFieldValue]
	);

	const fetchNearestSlot = useCallback(
		async ({ takeaway = false, branchId = null } = {}) => {
			try {
				const rawDates = fulfillment?.scheduled_delivery_dates || fulfillment?.takeaway_dates || fulfillment?.dates;
				const dates = (Array.isArray(rawDates) ? rawDates : [])
					.filter((d) => d?.available !== false)
					.map((d) => (typeof d === 'string' ? d : d.date))
					.filter(Boolean);
				if (!dates.length) return {};
				const nearestDate = dates[0];

				const tryFetch = async (type) => {
					const params = { type, date: nearestDate, ...(branchId ? { branchId } : {}) };
					const res = await api.get({ url: 'fulfillment/slots', params });
					return res?.slots || [];
				};

				const primaryType = takeaway ? 'takeaway' : 'scheduled_delivery';
				const fallbackType = takeaway ? 'scheduled_delivery' : null;

				let slots = await tryFetch(primaryType);
				if (!slots.length && fallbackType) slots = await tryFetch(fallbackType);

				if (!takeaway && fulfillment?.asap_available) {
					return {
						fulfillmentType: 'asap',
						scheduledDate: null,
						scheduledTime: null,
					};
				}

				return {
					fulfillmentType: takeaway ? undefined : 'delivery',
					scheduledDate: nearestDate,
					scheduledTime: slots[0] || null,
				};
			} catch {
				return {};
			}
		},
		[fulfillment]
	);

	const handleBranchSelect = useCallback(
		async (branch) => {
			const slot = await fetchNearestSlot({ takeaway: true, branchId: branch.id });
			const updatedData = { ...slot, fulfillmentType: 'takeaway', fulfillmentBranchId: branch.id };
			fillCheckoutData(updatedData);
			getCart(updatedData, false);
			closeDropdown();
			router.push(config.routes.menu.path);
		},
		[fillCheckoutData, getCart, fetchNearestSlot, closeDropdown, router]
	);

	const handleAddressSelect = useCallback(
		async (address) => {
			selectAddress({ checkedId: address.id, ...(hasAddressType ? { type: 'shipping' } : {}) });
			const slot = await fetchNearestSlot({ takeaway: false });
			const updatedData = { ...slot, addressId: address.id };
			fillCheckoutData(updatedData);
			getCart(updatedData, false);
			syncSearchFieldValue(formatAddressLabel(address));
			closeDropdown();
			router.push(config.routes.menu.path);
		},
		[selectAddress, fillCheckoutData, getCart, fetchNearestSlot, hasAddressType, closeDropdown, router, syncSearchFieldValue]
	);

	const handleSuggestSelect = useCallback(
		async (suggestion) => {
			const prefill = await resolveSuggestionPrefill(suggestion);
			resetSessionToken();
			openAddressFormPopup(prefill);
		},
		[openAddressFormPopup, resetSessionToken]
	);

	const handleCurrentLocation = useCallback(
		(e) => {
			e?.stopPropagation?.();
			if (!navigator.geolocation) return;

			setIsLocating(true);
			closeDropdown();

			navigator.geolocation.getCurrentPosition(
				async ({ coords }) => {
					let prefill = {};
					try {
						prefill = await resolveGeocodePrefill(coords, selectedLang);
					} catch {
						// geocode failed — open form empty
					} finally {
						setIsLocating(false);
					}
					openAddressFormPopup(prefill);
				},
				() => setIsLocating(false),
				{ enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
			);
		},
		[selectedLang, closeDropdown, openAddressFormPopup]
	);

	const handleSearchFocus = useCallback(() => {
		setShowDropdown(true);
		syncSearchFieldValue('');
		closeBottomPopups();
	}, [syncSearchFieldValue]);

	const handleSearchChange = useCallback(
		(e) => {
			const value = e?.target?.value ?? '';
			setShowDropdown(true);

			if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);

			if (!value.trim()) {
				clearSuggestions();
				return;
			}

			debounceTimeoutRef.current = setTimeout(() => fetchSuggestions(value), 300);
		},
		[clearSuggestions, fetchSuggestions]
	);

	const toggleDropdown = useCallback(() => {
		if (showDropdown) {
			closeDropdown();
			return;
		}
		syncSearchFieldValue('');
		setShowDropdown(true);
		focusSearchInput();
		closeBottomPopups();
	}, [showDropdown, closeDropdown, syncSearchFieldValue, focusSearchInput]);

	const takeawayPlaceholder = useMemo(() => (selectedBranch ? normalizeBranchText(selectedBranch.name) : translate('deliveryTemplate.selectShopLocation')), [selectedBranch, translate]);

	useEffect(() => {
		if (!showDropdown) return;
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				closeDropdown();
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [showDropdown, closeDropdown]);

	useEffect(() => () => clearSuggestions(), [clearSuggestions]);

	useEffect(() => {
		const handler = (e) => {
			const data = e.detail;
			setPendingAddress(data);
			if (data?.address) syncSearchFieldValue(data.address);
			router.push(config.routes.menu.path);
		};
		window.addEventListener('pendingAddressSaved', handler);
		return () => window.removeEventListener('pendingAddressSaved', handler);
	}, [router, syncSearchFieldValue]);

	useEffect(() => {
		if (showDropdown || isSearching) return;
		if (selectedAddress) {
			syncSearchFieldValue(formatAddressLabel(selectedAddress));
		} else if (!isLoggedIn && pendingAddress?.address) {
			syncSearchFieldValue(pendingAddress.address);
		} else {
			syncSearchFieldValue('');
		}
	}, [selectedAddress, showDropdown, isSearching, syncSearchFieldValue, isLoggedIn, pendingAddress]);

	const handlePendingAddressSelect = useCallback(async () => {
		if (!pendingAddress) return;
		const slot = await fetchNearestSlot({ takeaway: false });
		fillCheckoutData(slot);
		getCart(slot, false);
		setActiveTab('delivery');
		syncSearchFieldValue(pendingAddress.address || '');
		closeDropdown();
		router.push(config.routes.menu.path);
	}, [pendingAddress, fillCheckoutData, getCart, fetchNearestSlot, syncSearchFieldValue, closeDropdown, router]);

	// On first load, AddressProvider auto-selects the user's default saved
	// address for display, but only updates checkoutData.addressId — it never
	// refreshes the cart, so the mini cart's shipping cost stays empty until
	// the address is re-confirmed through the popup. Sync it here once.
	const syncedAddressIdRef = useRef(null);
	useEffect(() => {
		if (isTakeaway || !selectedAddress?.id) return;
		if (checkoutData?.fulfillmentType === 'takeaway') return;
		if (syncedAddressIdRef.current === selectedAddress.id) return;
		if (checkoutData?.addressId === selectedAddress.id) return;

		syncedAddressIdRef.current = selectedAddress.id;

		(async () => {
			const slot = await fetchNearestSlot({ takeaway: false });
			const updatedData = { ...slot, addressId: selectedAddress.id };
			fillCheckoutData(updatedData);
			getCart(updatedData, false);
		})();
	}, [selectedAddress?.id, isTakeaway, checkoutData?.fulfillmentType, checkoutData?.addressId, fetchNearestSlot, fillCheckoutData, getCart]);

	return {
		isDeliveryEnabled,
		activeTab,
		showDropdown,
		isLocating,
		isSuggestLoading,
		isSearching,
		dropdownRef,
		isTakeaway,
		branches,
		selectedBranch,
		addressList,
		addressLoading,
		addressSuggestions,
		selectedAddress,
		takeawayPlaceholder,
		handleTabChange,
		handleBranchSelect,
		handleAddressSelect,
		handleSuggestSelect,
		handleCurrentLocation,
		handleSearchFocus,
		handleSearchChange,
		toggleDropdown,
		isLoggedIn,
		pendingAddress,
		handlePendingAddressSelect,
	};
};
